/**
 * Homly — AIRecommendation
 * ========================
 * Composant d'affichage d'une reco IA actionnable.
 * 
 * Le brief est clair : "L'IA doit toujours aider l'utilisateur à passer à l'action."
 * Format : icône IA + insight contextualisé (langage humain) + bouton d'action concret
 *
 * Exemple :
 *   <AIRecommendation
 *     headline="M. Dupont consulte peu son espace depuis 10 jours"
 *     explanation="Proposez-lui un point téléphonique pour éviter une perte d'engagement."
 *     action={{ label: "Créer une relance", onClick: ... }}
 *     dismissable
 *   />
 */
import React from 'react';
import { Sparkles, X, ChevronRight } from 'lucide-react';

export function AIRecommendation({
    headline,
    explanation,
    action,                  // { label, onClick, icon }
    secondaryAction,         // { label, onClick }
    dismissable = false,
    onDismiss,
    confidence,              // optionnel : 0-100
    severity = 'medium',     // 'critical' | 'medium' | 'positive'
    className = '',
    dataTestid = 'ai-recommendation',
}) {
    const severityConfig = {
        critical: {
            color: 'var(--homly-priority-critical)',
            bg: 'rgba(255, 95, 87, 0.05)',
            border: 'rgba(255, 95, 87, 0.25)',
        },
        medium: {
            color: 'var(--homly-purple)',
            bg: 'rgba(168, 85, 247, 0.05)',
            border: 'rgba(168, 85, 247, 0.25)',
        },
        positive: {
            color: 'var(--homly-priority-ok)',
            bg: 'rgba(34, 211, 162, 0.05)',
            border: 'rgba(34, 211, 162, 0.25)',
        },
    };
    const cfg = severityConfig[severity] || severityConfig.medium;

    return (
        <article
            className={className}
            data-testid={dataTestid}
            style={{
                position: 'relative',
                display: 'flex',
                gap: 'var(--homly-space-4)',
                padding: 'var(--homly-space-5)',
                background: cfg.bg,
                border: `1px solid ${cfg.border}`,
                borderRadius: 'var(--homly-radius-lg)',
                fontFamily: 'var(--homly-font-body)',
                overflow: 'hidden',
            }}
        >
            {/* Halo accent */}
            <div
                aria-hidden="true"
                style={{
                    position: 'absolute',
                    top: -40, left: -40,
                    width: 120, height: 120,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${cfg.color}25 0%, transparent 70%)`,
                    pointerEvents: 'none',
                }}
            />

            {/* Icône IA */}
            <div
                style={{
                    position: 'relative',
                    flexShrink: 0,
                    width: 40,
                    height: 40,
                    borderRadius: 'var(--homly-radius-md)',
                    background: 'var(--homly-gradient-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 16px ${cfg.color}40`,
                }}
            >
                <Sparkles size={18} color="white" strokeWidth={2.2} aria-hidden="true" />
            </div>

            {/* Contenu */}
            <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                {/* Kicker IA */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 8,
                        flexWrap: 'wrap',
                    }}
                >
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontFamily: 'var(--homly-font-display)',
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.16em',
                            textTransform: 'uppercase',
                            color: cfg.color,
                        }}
                    >
                        <span
                            style={{
                                width: 5, height: 5, borderRadius: '50%',
                                background: cfg.color,
                                boxShadow: `0 0 8px ${cfg.color}`,
                            }}
                            aria-hidden="true"
                        />
                        Suggestion de l'IA
                    </span>
                    {confidence != null && (
                        <span
                            style={{
                                fontSize: 10,
                                color: 'var(--homly-text-tertiary)',
                                fontFamily: 'var(--homly-font-mono)',
                            }}
                        >
                            confiance · {confidence}%
                        </span>
                    )}
                </div>

                {/* Headline (l'observation factuelle) */}
                <p
                    style={{
                        margin: 0,
                        fontFamily: 'var(--homly-font-display)',
                        fontSize: 15,
                        fontWeight: 600,
                        lineHeight: 1.35,
                        color: 'var(--homly-text-primary)',
                    }}
                >
                    {headline}
                </p>

                {/* Explanation (le conseil actionnable) */}
                {explanation && (
                    <p
                        style={{
                            margin: '6px 0 0 0',
                            fontSize: 13.5,
                            lineHeight: 1.55,
                            color: 'var(--homly-text-secondary)',
                        }}
                    >
                        {explanation}
                    </p>
                )}

                {/* Actions */}
                {(action || secondaryAction) && (
                    <div
                        style={{
                            display: 'flex',
                            gap: 'var(--homly-space-2)',
                            marginTop: 'var(--homly-space-4)',
                            flexWrap: 'wrap',
                        }}
                    >
                        {action && (
                            <button
                                type="button"
                                onClick={action.onClick}
                                data-testid={`${dataTestid}-action`}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '8px 14px',
                                    background: cfg.color,
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: 'var(--homly-radius-md)',
                                    fontFamily: 'var(--homly-font-display)',
                                    fontSize: 12.5,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    boxShadow: `0 4px 12px ${cfg.color}40`,
                                    transition: 'transform var(--homly-transition-fast)',
                                }}
                                onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.97)'; }}
                                onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                            >
                                {action.icon && <action.icon size={14} strokeWidth={2.5} />}
                                {action.label}
                                <ChevronRight size={14} strokeWidth={2.5} />
                            </button>
                        )}
                        {secondaryAction && (
                            <button
                                type="button"
                                onClick={secondaryAction.onClick}
                                data-testid={`${dataTestid}-secondary`}
                                style={{
                                    padding: '8px 14px',
                                    background: 'transparent',
                                    color: 'var(--homly-text-secondary)',
                                    border: '1px solid var(--homly-border)',
                                    borderRadius: 'var(--homly-radius-md)',
                                    fontFamily: 'var(--homly-font-display)',
                                    fontSize: 12.5,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    transition: 'color var(--homly-transition-fast)',
                                }}
                            >
                                {secondaryAction.label}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Bouton dismiss */}
            {dismissable && (
                <button
                    type="button"
                    onClick={onDismiss}
                    aria-label="Ignorer cette suggestion"
                    data-testid={`${dataTestid}-dismiss`}
                    style={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--homly-text-tertiary)',
                        padding: 6,
                        borderRadius: 'var(--homly-radius-sm)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <X size={14} strokeWidth={2.5} />
                </button>
            )}
        </article>
    );
}
